import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  CheckCircle,
  Tag,
  Wallet,
  Send,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { RENTAL_PRODUCTS } from "@/data/products";
import RentPickerModal from "@/components/RentPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const advantages = [
  {
    icon: MapPin,
    title: "Flexible Abholung",
    text: "Abholung in Haibach im Mühlkreis – individuell nach Vereinbarung.",
  },
  {
    icon: CheckCircle,
    title: "Einsatzbereites Equipment",
    text: "Gepflegtes & sofort einsatzbereites Profi-Equipment für dein Projekt.",
  },
  {
    icon: Wallet,
    title: "Faire Tarife",
    text: "Faire Tarife & attraktive Paketpreise bei Kombi-Miete.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function HomePage() {
  const [loading, setLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % RENTAL_PRODUCTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Anfrage gesendet!", {
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });
      setFormData({ first_name: "", last_name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full mb-6">
                <Tag className="h-4 w-4" />
                Verleih in Haibach im Mühlkreis
              </span>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-950 tracking-tight leading-tight mb-6">
                Anhänger & Profi-Werkzeuge
                <span className="text-orange-600"> einfach mieten</span>
              </h1>

              <p className="text-lg text-zinc-600 leading-relaxed mb-8">
                Dein flexibler Verleih in Haibach im Mühlkreis / Raum Linz. Vom Transportanhänger
                bis zum Renovierungsgerät – unkompliziert, flexibel & fair.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/anhaenger">
                  <Button
                    size="lg"
                    className="bg-orange-600 hover:bg-orange-700 text-white px-8 w-full sm:w-auto"
                    data-testid="hero-cta-primary"
                  >
                    Anhänger mieten
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  className="border-zinc-300 text-zinc-900 hover:bg-zinc-100 w-full sm:w-auto"
                  data-testid="hero-cta-secondary"
                >
                  Alle Geräte entdecken
                </Button>
              </div>
            </motion.div>

            {/* Right Image Slider */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-zinc-100 h-[350px] lg:h-[450px]">
                {RENTAL_PRODUCTS.map((product, idx) => (
                  <Link
                    key={product.id}
                    to={product.href}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      idx === activeSlide ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                    data-testid={`hero-slide-${idx}`}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className={`w-full h-full ${
                        product.imageFit === "contain" ? "object-contain bg-white p-6" : "object-cover"
                      }`}
                    />
                  </Link>
                ))}

                {/* Dots */}
                <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                  {RENTAL_PRODUCTS.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSlide(idx)}
                      aria-label={`Slide ${idx + 1}`}
                      data-testid={`hero-dot-${idx}`}
                      className={`h-2.5 rounded-full transition-all ${
                        idx === activeSlide ? "w-7 bg-orange-600" : "w-2.5 bg-white/80 hover:bg-white"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Price badge (synced to active slide) */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-zinc-100 min-w-[110px]">
                <div className="text-center" data-testid="hero-price-badge">
                  <p className="text-sm text-zinc-500 mb-1">ab</p>
                  <p className="text-3xl font-bold text-orange-600">€{RENTAL_PRODUCTS[activeSlide].priceFrom}</p>
                  <p className="text-sm text-zinc-500">/ 4 Stunden</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Overview Grid */}
      <section className="bg-zinc-50 py-16 lg:py-24" data-testid="product-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight mb-4">
              Unser Mietangebot
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Entdecke unsere aktuellen Transportlösungen und Mietgeräte für dein Projekt.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {RENTAL_PRODUCTS.map((product, idx) => (
              <motion.div
                key={product.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden flex flex-col hover:shadow-xl hover:border-orange-200 transition-all duration-300"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-[4/3] bg-zinc-50 overflow-hidden flex items-center justify-center">
                  <img
                    src={product.image}
                    alt={product.name}
                    className={`w-full h-full ${
                      product.imageFit === "contain" ? "object-contain p-5" : "object-cover"
                    }`}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="inline-flex self-start items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full mb-3">
                    <Tag className="h-3 w-3" />
                    {product.tag}
                  </span>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed mb-5 flex-1">{product.info}</p>
                  <div className="flex items-end justify-between mb-5">
                    <div>
                      <p className="text-xs text-zinc-500">Preis ab</p>
                      <p className="text-2xl font-bold text-orange-600">€{product.priceFrom}</p>
                      <p className="text-xs text-zinc-500">/ 4 Std.</p>
                    </div>
                  </div>
                  <Link to={product.href}>
                    <Button
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid={`product-cta-${product.id}`}
                    >
                      Details & Buchen
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="bg-white py-16 lg:py-24" data-testid="advantages-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight">
              Warum bei rentmich.at mieten?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((adv, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="text-center p-8 rounded-2xl bg-zinc-50 border border-zinc-100"
              >
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <adv.icon className="h-7 w-7 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">{adv.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{adv.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Booking Request */}
      <section className="bg-zinc-950 py-16 lg:py-24" data-testid="home-contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              Anfrage senden
            </h2>
            <p className="text-lg text-zinc-400 max-w-xl mx-auto">
              Fragen zu einem Gerät oder zur Verfügbarkeit? Schreib uns – wir melden uns
              schnellstmöglich bei dir.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl p-8 border border-zinc-200"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Vorname *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Max"
                    className="border-zinc-200"
                    data-testid="home-input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nachname *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Mustermann"
                    className="border-zinc-200"
                    data-testid="home-input-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="max@beispiel.at"
                    className="border-zinc-200"
                    data-testid="home-input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+43 ..."
                    className="border-zinc-200"
                    data-testid="home-input-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Nachricht *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Welches Gerät interessiert dich? Wunschzeitraum, Fragen, etc."
                  className="border-zinc-200 resize-none"
                  rows={4}
                  data-testid="home-input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6"
                data-testid="home-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Anfrage senden
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <RentPickerModal open={pickerOpen} onOpenChange={setPickerOpen} />
    </div>
  );
}
